import React, { useState, useEffect } from 'react';
import { X, Download, Loader2, Search, ExternalLink, Eye, EyeOff, Link, ChevronUp, ChevronDown } from 'lucide-react';
import { useRecipeStore } from '../store/recipeStore';
import { ErrorBoundary } from './ErrorBoundary';
import { extractRecipe } from '../services/recipe';

interface RecipeImportModalProps {
  onClose: () => void;
  sourceUrl?: string;
}

export function RecipeImportModal({ onClose, sourceUrl }: RecipeImportModalProps) {
  const [url, setUrl] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<any | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showSearch, setShowSearch] = useState(true);
  const { addRecipe } = useRecipeStore();

  useEffect(() => {
    // Prevent body scrolling when modal is open
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  useEffect(() => {
    if (sourceUrl) {
      handlePreview(sourceUrl);
    }
  }, [sourceUrl]);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    try {
      window.open(`https://www.google.com/search?q=${encodeURIComponent(searchQuery + ' recipe')}`, '_blank');
    } catch (err) {
      console.error('Search error:', err);
      setError(err instanceof Error ? err.message : 'Failed to open search');
    }
  };

  const handleImport = async (importUrl: string = url) => {
    if (!importUrl) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const recipe = await extractRecipe(importUrl);
      await addRecipe(recipe);
      onClose();
    } catch (err) {
      console.error('Failed to import recipe:', err);
      setError(err instanceof Error ? err.message : 'Failed to import recipe');
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async (previewUrl: string) => {
    if (!previewUrl) return;
    
    setLoading(true);
    setError(null);
    setShowPreview(true);
    
    try {
      const recipe = await extractRecipe(previewUrl);
      setPreview(recipe);
      setShowSearch(false);
    } catch (err) {
      console.error('Failed to preview recipe:', err);
      setError(err instanceof Error ? err.message : 'Failed to preview recipe');
      setPreview(null);
    } finally {
      setLoading(false);
    }
  };

  const renderPreview = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center min-h-[400px] text-gray-500">
          <div className="animate-pulse">Loading preview...</div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[400px] p-6 text-center">
          <div className="text-red-600 mb-4">{error}</div>
          <a 
            href={url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800"
          >
            Open recipe in new tab
          </a>
        </div>
      );
    }

    if (!preview || !showPreview) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[400px] p-6 text-center text-gray-500">
          <h3 className="text-xl font-medium mb-4">How to Import a Recipe</h3>
          <div className="space-y-6 max-w-md">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                1
              </div>
              <div className="text-left">
                <h4 className="font-medium mb-2">Search for a Recipe</h4>
                <p className="text-gray-600">
                  Use the search box above to find recipes on Google. This will open in a new tab.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                2
              </div>
              <div className="text-left">
                <h4 className="font-medium mb-2">Copy the Recipe URL</h4>
                <p className="text-gray-600">
                  When you find a recipe you like, copy its URL from your browser's address bar.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                3
              </div>
              <div className="text-left">
                <h4 className="font-medium mb-2">Preview and Import</h4>
                <p className="text-gray-600">
                  Paste the URL in the import box and click preview. If it looks good, click Import!
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="space-y-4">
          <h1 className="text-2xl font-bold">{preview.title}</h1>
          
          {preview.rating && (
            <div className="space-y-1">
              <div className="text-xl text-yellow-500">
                {'★'.repeat(Math.floor(preview.rating.value))}
                {preview.rating.value % 1 >= 0.5 ? '½' : ''}
                {'☆'.repeat(5 - Math.ceil(preview.rating.value))}
              </div>
              <div className="text-sm text-gray-600">
                {preview.rating.value.toFixed(1)} out of 5 ({preview.rating.count} {preview.rating.count === 1 ? 'review' : 'reviews'})
                {preview.rating.source && ` • ${preview.rating.source}`}
              </div>
            </div>
          )}
          
          {preview.image_url && (
            <img 
              src={preview.image_url} 
              alt={preview.title}
              className="w-full h-48 object-cover rounded-lg"
            />
          )}
        </div>

        <div>
          <h2 className="text-lg font-semibold mb-3">Ingredients</h2>
          <ul className="space-y-2">
            {preview.ingredients.map((ing: any, idx: number) => (
              <li key={idx} className="text-gray-700 border-b border-gray-100 pb-2">
                {ing.name}
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h2 className="text-lg font-semibold mb-3">Instructions</h2>
          <ol className="space-y-4 list-decimal list-inside">
            {preview.instructions.map((step: string, idx: number) => (
              <li key={idx} className="text-gray-700">
                {step}
              </li>
            ))}
          </ol>
        </div>

        {preview.reviews && preview.reviews.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold mb-3">Reviews</h2>
            <div className="space-y-4">
              {preview.reviews.slice(0, 3).map((review: any, idx: number) => (
                <div key={idx} className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="space-y-1">
                      <div className="text-yellow-500">
                        {'★'.repeat(Math.floor(review.rating))}
                        {review.rating % 1 >= 0.5 ? '½' : ''}
                        {'☆'.repeat(5 - Math.ceil(review.rating))}
                      </div>
                      <div className="font-medium">{review.author}</div>
                    </div>
                    <div className="text-sm text-gray-500">
                      {new Date(review.date).toLocaleDateString()}
                    </div>
                  </div>
                  <p className="text-gray-700">{review.text}</p>
                </div>
              ))}
              {preview.reviews.length > 3 && (
                <div className="text-center">
                  <a
                    href={preview.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800"
                  >
                    View all {preview.reviews.length} reviews
                  </a>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="pt-4 border-t border-gray-200 text-sm text-gray-500">
          Source:{' '}
          <a
            href={preview.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800"
          >
            {preview.source_url}
          </a>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-x-0 bottom-0 z-50 transform transition-transform md:inset-0 md:flex md:items-center md:justify-center">
        <div className="bg-white w-full min-h-[50vh] max-h-[90vh] rounded-t-xl md:rounded-xl md:w-[95%] md:max-w-[1400px] md:min-h-0 flex flex-col shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <h2 className="text-xl font-semibold">Import Recipe</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Search and Import Section */}
            <div className="border-b">
              <div className="p-4">
                <button
                  onClick={() => setShowSearch(!showSearch)}
                  className="w-full flex items-center justify-between text-left"
                >
                  <h3 className="text-lg font-medium">Search & Import</h3>
                  {showSearch ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
              </div>
              
              {showSearch && (
                <div className="px-4 pb-4 space-y-6">
                  {/* Recipe Search */}
                  <div>
                    <h3 className="text-lg font-medium mb-4">Search for Recipes</h3>
                    <form onSubmit={handleSearch} className="space-y-4">
                      <div className="flex gap-2">
                        <div className="flex-1 relative">
                          <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search for recipes..."
                            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                        </div>
                        <button
                          type="submit"
                          disabled={!searchQuery.trim()}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                        >
                          <Search size={20} />
                          <span className="hidden sm:inline">Search</span>
                        </button>
                      </div>
                      <p className="text-sm text-gray-600">
                        Search will open in a new tab. Find a recipe you like, then copy its URL.
                      </p>
                    </form>
                  </div>

                  {/* Manual URL Input */}
                  <div>
                    <h3 className="text-lg font-medium mb-4">Import from URL</h3>
                    <div className="space-y-4">
                      <div className="flex gap-2">
                        <div className="flex-1 relative">
                          <input
                            type="url"
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            placeholder="Paste recipe URL here"
                            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          <Link className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                        </div>
                        <button
                          onClick={() => url && handlePreview(url)}
                          disabled={!url}
                          className="px-3 py-2 text-gray-600 hover:text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-50"
                          title="Preview URL"
                        >
                          {showPreview ? <EyeOff size={20} /> : <Eye size={20} />}
                        </button>
                      </div>
                      <p className="text-sm text-gray-600">
                        Paste a recipe URL to import it directly into your collection.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Preview Section */}
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="p-4 bg-white border-b flex items-center justify-between">
                <h3 className="font-medium">Preview</h3>
                {url && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowPreview(!showPreview)}
                      className="p-2 text-gray-600 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                      title={showPreview ? "Hide Preview" : "Show Preview"}
                    >
                      {showPreview ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-gray-600 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                      title="Open in new tab"
                    >
                      <ExternalLink size={20} />
                    </a>
                  </div>
                )}
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                <ErrorBoundary>
                  {renderPreview()}
                </ErrorBoundary>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 border-t flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={() => handleImport(url || sourceUrl)}
              disabled={loading || (!url && !sourceUrl)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              <Download size={20} />
              <span className="hidden sm:inline">Import Recipe</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
}